"use client"

import { createContext, useContext, useState, useEffect, useMemo, useRef, ReactNode, Suspense } from "react"
import { useStytchUser } from "@stytch/nextjs"
import { getSupabase, setSupabaseAccessToken } from "@/lib/supabase"
import type { PostgrestSingleResponse } from "@supabase/supabase-js"
import { requestSessionRefresh, resolveSessionRefresh, setTokenFetchUrl } from "@/lib/session-refresh-registry"
import { FlowchartNode } from "@/components/sequences/types"
import { useRouter } from "next/navigation"
import { isB2BRoute, isAdminUser } from "@/lib/auth"
import {
    getAdminOrgImpersonationDomain,
} from "@/lib/auth/admin-org-impersonation"
import { getB2BSessionBridge } from "@/lib/auth/b2b-session-bridge"

type ViewMode = "individual" | "team"

interface OrganizationContextType {
    organization: Organization | null
    loading: boolean
    error: string | null
    userEmail: string
    isAdmin: boolean
    isOrgAdmin: boolean
    viewMode: ViewMode
    setViewMode: (mode: ViewMode) => void
    clearCache: () => void
    refetchOrganization: () => Promise<void>
    updateOnboardingState: (patch: Partial<OnboardingState>) => Promise<void>
}

export type MigrationNoticeId = "migration_walkthrough_v1"

export interface MigrationNoticeRecord {
    first_seen_at: string
    dismissed_at: string | null
}

export interface OnboardingState {
    started_at: string | null
    dismissed_at: string | null
    // Set the first time the user sees (and acknowledges) the welcome modal.
    // Once set, the modal never reappears.
    welcome_seen_at?: string | null
    // Per-user map (keyed by lowercase email) so dismissals are personal even
    // though onboarding_state is an org-wide column.
    migration_notices?: Record<string, Partial<Record<MigrationNoticeId, MigrationNoticeRecord>>>
}

export interface Persona {
    id: string
    name: string
    title?: string
    emailAccountIds: string[] // unipile_ids or email addresses
    linkedinAccountIds: string[] // unipile_ids
    twitterAccountIds: string[] // twitter_user_ids
    created_at?: string
    pendingEmailAccountAddresses?: string[]
    /** Hosted profile photo URL. When set, replaces the colored-initials
     *  avatar everywhere this persona renders. */
    profilePictureUrl?: string
    /** Chosen initials-avatar background (a hex from PERSONA_AVATAR_PALETTE).
     *  When unset, the color is derived deterministically from the name. */
    avatarColor?: string
}

export interface TwitterAccount {
    twitterAccountId: string
    twitterUsername?: string
    connectedAt?: string
    tokenExpiresAt?: string
}

export interface TeamMember {
    url: string
    name?: string
    context?: string
}

export interface ICPPersona {
    id: string
    content: string
    name: string
    created_at: string
}

export interface CompanyProfileICP {
    description: string
    titles: string[]
    industries: string[]
    sizes: string[]
    locations: string[]
}

export interface CompanyProfile {
    website: string
    name: string
    description: string
    industry: string
    companyType?: string
    sellsTo?: string
    notableCustomers?: string
    socialProof?: string
    icp: CompanyProfileICP
}

interface ParsedEmailAccount {
    email: string
    status: string
    unipileAccountId?: string
}

export interface Sequence {
    id: string
    name: string
    leadId: string // ID of the lead list this sequence is for
    sequence: FlowchartNode[]
    campaignID?: string
    isActive?: boolean
    isLegacy?: boolean // Flag to identify legacy imported sequences
    csvName?: string // CSV file name for legacy sequences
    domain?: string // Domain for legacy sequences
}
interface UnipileAccount {
    email: string
    unipile_id: string
    signature?: string
    emailsPerDay?: number
    first_name?: string
    status?: string
    provider?: string
    name?: string
    warming_since?: string
    /** Unipile stamps this when an OAuth refresh / cookie failure flips the
     *  account into a non-sending state. Used to surface a "Disconnected"
     *  pill in MessageLimitsTracker — sending through the inbox is blocked
     *  until the user reconnects. Mirrors the field on
     *  `lib/sequenceTypes.ts#UnipileAccount`. */
    disconnected_at?: string
    disconnection_reason?: string
    sending_frozen?: boolean
    email_reputation?: {
        email_quality?: {
            score: number
            is_spf_strict: boolean
            is_dmarc_enforced: boolean
        }
        email_deliverability?: {
            is_smtp_valid: boolean
            is_mx_valid: boolean
            status: string
            status_detail: string
        }
        email_risk?: {
            address_risk_status: string
            domain_risk_status: string
        }
        email_breaches?: {
            total_breaches: number
        }
    }
}

export interface Organization {
    domain: string
    name: string | null
    email_accounts: ParsedEmailAccount[] | null
    linkedin_accounts:
    | {
        email: string
        unipileAccountId: string
        profile: string | null
        limits?: {
            maxFollowsPerDay: number
            maxMessagesPerDay: number
            maxInMailMessagesPerDay: number
            maxConnectionRequestsPerDay: number
            maxProfileViewsPerDay: number
            maxPostLikesPerDay: number
        }
        weekly_connection_limit?: number
        connection_count?: number
        /** ISO timestamp set by the LinkedIn-connect backend when the account is first linked. */
        connectedAt?: string
        /** Auth health flag mirrored from Unipile — anything other than
         *  "connected" (case-insensitive) means the account can't send.
         *  This is the same field the Connectors page (AccountsView)
         *  reads to render the red "Disconnected" pill. */
        status?: string
        /** Set alongside `status = "disconnected"` so the UI can show
         *  *why* (e.g. "user_initiated" vs. "Expired credentials (401
         *  from Unipile)"). Cleared on reconnect. */
        disconnected_at?: string
        disconnection_reason?: string
        /** Auto-populated from Unipile /users/me at connect time
         *  (server/linkedin_account_enrichment.py). Photo is mirrored into
         *  the signature-images bucket so it survives LinkedIn URL
         *  expiration; first/last/headline come from the same call so the
         *  buy-inbox flow can pre-fill without a second Fiber lookup. */
        profilePictureUrl?: string
        first_name?: string
        last_name?: string
        headline?: string
        public_identifier?: string
    }[]
    | null
    sales_nav_lists: Array<{
        id?: string
        list_name: string
        sales_nav_url?: string
        csvFilename?: string
        lead_count?: number
        created_at?: string
    }> | null
    sequences: Sequence[] | null
    linkedin_ids: string[] | null
    heyreach_id: string | null
    account_name: string | null
    unipile_accounts: UnipileAccount[] | null
    personas: Persona[] | null
    templates?: string[] | null // Array of custom email template texts
    slack_webhook_url?: string | null // Slack webhook URL for notifications
    active_website_visitors?: boolean | null // Whether website visitor tracking is enabled
    team_linkedin_urls?: string[] | null // Array of team member LinkedIn URLs for finding commonalities (legacy)
    team_members?: TeamMember[] | null // Array of team members with LinkedIn URLs and context
    octolens_keywords?: string[] | null // Keywords mapped to this org for OctoLens online mentions
    twitter_accounts?: TwitterAccount[] | null
    admins?: string[] | null
    icp_personas?: ICPPersona[] | null
    company_icp_personas?: ICPPersona[] | null
    company_profile?: CompanyProfile | null
    auto_skip_empty_ai_columns?: boolean | null
    reply_agent_config?: {
        enabled: boolean
        company_name: string
        calendly_link: string
        value_prop: string
        slack_channel_id: string
    } | null
    email_sending_rules?: {
        recipient_rules: {
            pattern: string
            delay_min_seconds: number | null
            delay_max_seconds: number | null
            disable_bounce_protection: boolean
        }[]
    } | null
    onboarding_state?: OnboardingState | null
    created_at?: string | null
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined)

const CACHE_KEY = "cardinal_org_cache"
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes


function OrganizationProviderInner({ children }: { children: ReactNode }) {
    const { user, isInitialized } = useStytchUser()
    const router = useRouter()

    const [organization, setOrganization] = useState<Organization | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [supabaseReady, setSupabaseReady] = useState(false)

    // B2B fallback: when no B2C user, use credentials from the B2B session bridge.
    // `bridgeTick` is bumped by the b2b-bridge-changed listener so the bridge
    // gets re-read after B2BAuthProvider populates sessionStorage post-login.
    const [bridgeTick, setBridgeTick] = useState(0)
    const b2bBridge = useMemo(
        () => (!user ? getB2BSessionBridge() : null),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [user, bridgeTick],
    )
    const b2bToken = b2bBridge?.supabaseToken ?? null
    const userEmail = user?.emails?.[0]?.email || b2bBridge?.email || ""
    // MIGRATION AUX — B2B bridge carries the admin flag resolved from Stytch roles.
    // Once B2C is removed, replace this entire block with the B2B admin logic.
    const [b2bAdmin, setB2bAdmin] = useState<boolean | null>(b2bBridge?.isAdmin ?? null)

    useEffect(() => {
        const adminHandler = (e: Event) => {
            const detail = (e as CustomEvent<{ isAdmin: boolean }>).detail
            console.log("[diag] b2b-admin-changed received", { incoming: detail.isAdmin })
            setB2bAdmin(prev => {
                if (prev === detail.isAdmin) {
                    console.log("[diag] b2b-admin-changed no-op (same value)")
                    return prev
                }
                console.log("[diag] b2b-admin-changed FLIP", { prev, next: detail.isAdmin })
                return detail.isAdmin
            })
        }
        const bridgeHandler = () => setBridgeTick(t => t + 1)
        window.addEventListener("b2b-admin-changed", adminHandler)
        window.addEventListener("b2b-bridge-changed", bridgeHandler)
        return () => {
            window.removeEventListener("b2b-admin-changed", adminHandler)
            window.removeEventListener("b2b-bridge-changed", bridgeHandler)
        }
    }, [])

    // B2C session: derive admin from email allowlist only. `b2bAdmin === false` must not win here —
    // a stale `b2b-admin-changed` event would otherwise hide Organizations for Stytch B2C users.
    // B2B-only session (no Stytch user): use bridge flag, then fall back to email allowlist.
    const isAdmin = user ? isAdminUser(userEmail) : (b2bAdmin ?? isAdminUser(userEmail))
    const isOrgAdmin = !!(organization?.admins?.some(a => a.toLowerCase() === userEmail.toLowerCase()))

    // Underlying preference (preserved across sessions, used only for admins).
    // Non-admins are forced to "individual" so they only see their own workflows
    // and lists — this enforces the promise on the Organization Admins card.
    const VIEW_MODE_KEY = "cardinal_view_mode"
    const [storedViewMode, setStoredViewMode] = useState<ViewMode>(() => {
        if (typeof window === "undefined") return "team"
        return (localStorage.getItem(VIEW_MODE_KEY) as ViewMode) || "team"
    })

    // Override default for mintlify.com to "individual" on first visit
    useEffect(() => {
        if (!organization) return
        const stored = typeof window !== "undefined" ? localStorage.getItem(VIEW_MODE_KEY) : null
        if (!stored && organization.domain === "mintlify.com") {
            setStoredViewMode("individual")
        }
    }, [organization])

    // Effective view mode: admins use their stored preference; everyone else is
    // restricted to their own data. This is the value consumers should rely on.
    const viewMode: ViewMode = isOrgAdmin ? storedViewMode : "individual"

    const setViewMode = (mode: ViewMode) => {
        setStoredViewMode(mode)
        if (typeof window !== "undefined") {
            localStorage.setItem(VIEW_MODE_KEY, mode)
        }
    }

    // Clear cache (call on logout)
    const clearCache = () => {
        if (typeof window === "undefined") return
        sessionStorage.removeItem(CACHE_KEY)
    }

    /**
     * Fetch custom Supabase JWT from our API and set it on the client.
     * On 401, triggers on-demand session refresh and waits for token to be set.
     */
    const initSupabaseAuth = async () => {
        try {
            const params = new URLSearchParams()
            if (userEmail) {
                params.set("email", userEmail)
            }
            const impersonationDomain =
                typeof window !== "undefined" && isAdminUser(userEmail)
                    ? getAdminOrgImpersonationDomain()
                    : null
            if (impersonationDomain) {
                params.set("org", impersonationDomain)
            }
            const url = `/api/auth/supabase-token${params.toString() ? `?${params}` : ""}`
            setTokenFetchUrl(url)
            const res = await fetch(url, { credentials: "same-origin" })

            if (res.status === 401) {
                await requestSessionRefresh()
                // Re-fetch token after session refresh (the original request got 401)
                const retryRes = await fetch(url, { credentials: "same-origin" })
                if (retryRes.ok) {
                    const { token } = await retryRes.json()
                    setSupabaseAccessToken(token)
                }
                return retryRes.ok
            }
            if (!res.ok) {
                console.error("Failed to get Supabase token:", res.status)
                return false
            }

            const { token } = await res.json()
            setSupabaseAccessToken(token)
            return true
        } catch (err) {
            console.error("Error fetching Supabase token:", err)
            return false
        }
    }

    const fetchOrganization = async () => {
        if (!userEmail) {
            setLoading(false)
            return
        }

        try {
            const email = userEmail
            // B2B: org slug is the domain. B2C: email domain unless admin is impersonating another org.
            const impersonationDomain = isAdmin ? getAdminOrgImpersonationDomain() : null
            const domain =
                impersonationDomain || b2bBridge?.orgDomain || email.split("@")[1].toLowerCase()

            console.log(`Fetching organization for domain: ${domain}`)

            // Fetch fresh data - always get the latest client after token refresh
            const supabase = getSupabase()
            const orgFields = "domain, name, email_accounts, linkedin_accounts, sales_nav_lists, sequences, linkedin_ids, heyreach_id, account_name, unipile_accounts, personas, templates, slack_webhook_url, team_linkedin_urls, team_members, active_website_visitors, octolens_keywords, twitter_accounts, admins, icp_personas, company_icp_personas, reply_agent_config, email_sending_rules, onboarding_state, company_profile"

            const fetchWithRetry = async <T,>(fn: () => Promise<T>, timeoutMs = 10000, retries = 3): Promise<T> => {
                let lastErr: unknown
                for (let attempt = 0; attempt <= retries; attempt++) {
                    if (attempt > 0) await new Promise(r => setTimeout(r, 1000 * attempt))
                    try {
                        return await Promise.race([
                            fn(),
                            new Promise<never>((_, reject) =>
                                setTimeout(() => reject(new Error(`Organization fetch timed out`)), timeoutMs)
                            ),
                        ])
                    } catch (err) {
                        lastErr = err
                        console.warn(`[fetchOrganization] attempt ${attempt + 1} failed:`, err)
                    }
                }
                throw lastErr
            }

            // Try domain match first, then fall back to invited_emails
            type OrgFetchResult = PostgrestSingleResponse<Organization>

            let result: OrgFetchResult = await fetchWithRetry<OrgFetchResult>(() =>
                supabase
                    .from("organizations")
                    .select(orgFields)
                    .eq("domain", domain)
                    .single()
            )

            if (result.error && email) {
                const invited = await fetchWithRetry<OrgFetchResult>(() =>
                    supabase
                        .from("organizations")
                        .select(orgFields)
                        .contains("invited_emails", [email.toLowerCase()])
                        .single()
                )
                if (invited.data) {
                    result = invited
                }
            }

            if (result.error) {
                const rawErr = result.error as unknown
                const propNames = rawErr && typeof rawErr === "object"
                    ? Object.getOwnPropertyNames(rawErr)
                    : []
                // Build a plain object that captures EVERY own property (incl. non-enumerable)
                // so PostgrestError, native Error, and { } all show up legibly.
                const dump: Record<string, unknown> = {}
                for (const k of propNames) {
                    dump[k] = (rawErr as Record<string, unknown>)[k]
                }
                console.error("[fetchOrganization] error", {
                    domain,
                    email,
                    ctor: (rawErr as { constructor?: { name?: string } })?.constructor?.name,
                    typeofErr: typeof rawErr,
                    ownProps: propNames,
                    dump,
                    raw: rawErr,
                    str: String(rawErr),
                })
                const errMsg = (rawErr as { message?: string })?.message
                setError(errMsg ?? "Unknown error fetching organization")
                setOrganization(null)
            } else if (!result.data) {
                console.error("Organization row missing for domain:", domain)
                setError("Organization not found")
                setOrganization(null)
            } else {
                let data = result.data

                console.log("Fetched organization:", data.domain)

                // Auto-populate admins if empty — first user becomes admin
                // Uses .is("admins", null) to avoid race condition where multiple
                // users loading simultaneously could all become admins
                if ((!data.admins || data.admins.length === 0) && email) {
                    const supabase = getSupabase()
                    const { data: updateResult } = await supabase
                        .from("organizations")
                        .update({ admins: [email] })
                        .eq("domain", data.domain)
                        .is("admins", null)
                        .select("admins")
                        .single()

                    if (updateResult) {
                        data.admins = [email]
                        console.log("Auto-populated admins with:", email)
                    } else {
                        // Another user won the race — refetch to get their admins
                        const { data: refreshed } = await supabase
                            .from("organizations")
                            .select("admins")
                            .eq("domain", data.domain)
                            .single()
                        if (refreshed?.admins) {
                            data.admins = refreshed.admins
                        }
                    }
                }

                const normalizedPersonas = (data.personas || []).map((persona: Persona) => ({
                    ...persona,
                    emailAccountIds: persona.emailAccountIds || [],
                    linkedinAccountIds: persona.linkedinAccountIds || [],
                    twitterAccountIds: persona.twitterAccountIds || [],
                }))
                const normalizedOrganization: Organization = {
                    ...data,
                    personas: normalizedPersonas,
                    twitter_accounts: data.twitter_accounts || [],
                }

                setOrganization(normalizedOrganization)
                setError(null)

                // Cache the data
                try {
                    const cache = {
                        data: normalizedOrganization,
                        timestamp: Date.now(),
                        userEmail: email
                    }
                    sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache))
                } catch (e) {
                    console.error("Error caching data:", e)
                }
            }
        } catch (err) {
            console.error("Error in fetchOrganization:", err)
            setError("Failed to fetch organization")
            setOrganization(null)
        } finally {
            setLoading(false)
        }
    }

    // First: get the custom Supabase JWT when user authenticates
    // Depend on the stable email string, not the user object reference,
    // so Stytch SWR revalidation on tab focus doesn't re-trigger this.
    useEffect(() => {
        if (!isInitialized) {
            setLoading(true)
            return
        }

        if (!userEmail) {
            setSupabaseReady(false)
            setOrganization(null)
            setError(null)
            // Keep loading=true while we don't know the email yet. The two real
            // outcomes from here are: (a) B2BAuthProvider populates the bridge
            // and we re-enter this effect with a userEmail, or (b) the
            // redirect-to-/login effect below sends us away. In both cases the
            // skeleton is the right thing to show. Setting loading=false here
            // would flash "No organization found" during OAuth/magic-link
            // callbacks where the B2B cookie isn't set yet at the moment Stytch
            // B2C reports `isInitialized=true`.
            setLoading(true)
            return
        }

        // B2B fallback: the B2B context already minted a Supabase JWT —
        // set it on the B2C client so getSupabase() works with B2B credentials.
        if (b2bToken) {
            setTokenFetchUrl("/api/auth/b2b-supabase-token")
            setSupabaseAccessToken(b2bToken)
            setSupabaseReady(true)
            // Do NOT setLoading(false) here — fetchOrganization (triggered by
            // supabaseReady) is what actually loads the org row. Releasing the
            // loading gate now would flash "No organization found" for the
            // ~1 frame between auth-ready and org-fetched.
            return
        }

        setSupabaseReady(false) // Reset so org-fetch effect waits for new token
        setLoading(true)
        initSupabaseAuth().then(success => {
            setSupabaseReady(success)
            if (!success) {
                // On B2B routes the B2C-style init is expected to fail —
                // B2BAuthProvider handles the Supabase token via its own flow.
                if (!isB2BRoute()) {
                    console.error("Failed to initialize Supabase auth - queries may fail")
                }
                setSupabaseReady(true) // still proceed so the app isn't stuck
            }
        })
    }, [userEmail, isInitialized, b2bToken])

    useEffect(() => {
        if (!isInitialized) return
        if (!userEmail && !isB2BRoute()) {
            router.replace("/login")
        }
    }, [isInitialized, userEmail, router])

    // Second: fetch organization once Supabase session is set
    const fetchOrgEffectRunsRef = useRef(0)
    const lastDepsRef = useRef<{ supabaseReady: boolean; userEmail: string; isAdmin: boolean } | null>(null)
    useEffect(() => {
        fetchOrgEffectRunsRef.current += 1
        const prev = lastDepsRef.current
        const next = { supabaseReady, userEmail, isAdmin }
        const changed = prev
            ? Object.entries(next).filter(([k, v]) => (prev as any)[k] !== v).map(([k]) => k)
            : ["initial"]
        console.log(`[diag] fetchOrg effect fire #${fetchOrgEffectRunsRef.current}`, { changed, ...next })
        lastDepsRef.current = next
        if (supabaseReady) {
            fetchOrganization()
        }
    }, [supabaseReady, userEmail, isAdmin])

    // Re-initialize Supabase auth when Stytch session is refreshed (e.g. after 401)
    // Don't set loading=true here — the app stays mounted and refreshes silently.
    useEffect(() => {
        if (typeof window === "undefined" || !userEmail) return

        const handleSessionRefreshed = async () => {
            console.log("[diag] stytch-session-refreshed handler fired")
            try {
                const success = await initSupabaseAuth()
                if (success) {
                    await fetchOrganization()
                }
            } finally {
                resolveSessionRefresh()
            }
        }

        window.addEventListener("stytch-session-refreshed", handleSessionRefreshed)
        return () => window.removeEventListener("stytch-session-refreshed", handleSessionRefreshed)
    }, [userEmail])

    const updateOnboardingState = async (patch: Partial<OnboardingState>) => {
        if (!organization) return
        const next: OnboardingState = {
            started_at: organization.onboarding_state?.started_at ?? null,
            dismissed_at: organization.onboarding_state?.dismissed_at ?? null,
            welcome_seen_at: organization.onboarding_state?.welcome_seen_at ?? null,
            migration_notices: organization.onboarding_state?.migration_notices,
            ...patch,
        }
        // Optimistic update — Supabase write happens in the background.
        setOrganization({ ...organization, onboarding_state: next })
        const supabase = getSupabase()
        const { error: updateError } = await supabase
            .from("organizations")
            .update({ onboarding_state: next })
            .eq("domain", organization.domain)
        if (updateError) {
            console.error("Failed to persist onboarding_state:", updateError)
        }
    }

    const contextValue = useMemo(() => ({
        organization,
        loading,
        error,
        userEmail,
        isAdmin,
        isOrgAdmin,
        viewMode,
        setViewMode,
        clearCache,
        refetchOrganization: fetchOrganization,
        updateOnboardingState,
    }), [organization, loading, error, userEmail, isAdmin, isOrgAdmin, viewMode])

    return (
        <OrganizationContext.Provider value={contextValue}>
            {children}
        </OrganizationContext.Provider>
    )
}

export function OrganizationProvider({ children }: { children: ReactNode }) {
    return (
        <Suspense
            fallback={
                <OrganizationContext.Provider
                    value={{
                        organization: null,
                        loading: true,
                        error: null,
                        userEmail: "",
                        isAdmin: false,
                        isOrgAdmin: false,
                        viewMode: "individual",
                        setViewMode: () => { },
                        clearCache: () => { },
                        refetchOrganization: async () => { },
                        updateOnboardingState: async () => { },
                    }}
                >
                    {children}
                </OrganizationContext.Provider>
            }
        >
            <OrganizationProviderInner>{children}</OrganizationProviderInner>
        </Suspense>
    )
}

export function useOrganization() {
    const context = useContext(OrganizationContext)
    if (context === undefined) {
        throw new Error("useOrganization must be used within OrganizationProvider")
    }
    return context
}
